import { AlertCircleIcon, PlusIcon, ZapIcon } from 'lucide-react';
import { ReactNode } from 'react';

import { Badge } from '@/components/core/badge';
import { cn } from '@/shared/helpers';

// Presentational pieces of the canvas forest: pills, cards, and the static
// CSS/SVG connectors between them. Layout is plain flex flow — nodes are not
// freely positionable and variable-height cards must reflow their branch.

function NodeIcon({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary',
        className,
      )}
    >
      {children}
    </span>
  );
}

export function TriggerPill({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-full border bg-card py-1.5 pr-4 pl-1.5 shadow-xs">
      <NodeIcon>
        <ZapIcon className="size-3.5" />
      </NodeIcon>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

function IncompleteBadge() {
  return (
    <Badge variant="destructive" size="sm" className="ml-auto">
      <AlertCircleIcon />
      Incomplete
    </Badge>
  );
}

interface NodeCardProps {
  icon: ReactNode;
  title: string;
  summary: ReactNode;
  selected: boolean;
  onSelect: () => void;
  invalid?: boolean;
  className?: string;
}

export function NodeCard({
  icon,
  title,
  summary,
  selected,
  onSelect,
  invalid = false,
  className,
}: NodeCardProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-invalid={invalid || undefined}
      onClick={onSelect}
      className={cn(
        'w-64 rounded-xl border bg-card text-left shadow-xs transition-[border-color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
        selected
          ? 'border-primary ring-3 ring-primary/20'
          : invalid
            ? 'border-destructive ring-3 ring-destructive/20'
            : 'hover:border-muted-foreground/40',
        className,
      )}
    >
      <div className="flex items-center gap-2 px-3 pt-3">
        <NodeIcon className="rounded-md">{icon}</NodeIcon>
        <span className="text-sm font-medium">{title}</span>
        {invalid && <IncompleteBadge />}
      </div>
      <div className="px-3 pt-2 pb-3">
        <div className="rounded-lg border border-border/60 bg-muted/50 px-3 py-2 text-sm">
          {summary}
        </div>
      </div>
    </button>
  );
}

interface AddPillProps {
  icon: ReactNode;
  label: string;
  selected?: boolean;
  /** When true, the pill shows a destructive ring (an empty-but-required rule). */
  invalid?: boolean;
  onClick?: () => void;
}

/** Placeholder pill for an empty condition or action branch. */
export function AddPill({ icon, label, selected = false, invalid = false, onClick }: AddPillProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-invalid={invalid || undefined}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-full border bg-card py-1.5 pr-4 pl-1.5 text-sm font-medium whitespace-nowrap shadow-xs transition-[border-color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
        selected
          ? 'border-primary ring-3 ring-primary/20'
          : invalid
            ? 'border-destructive ring-3 ring-destructive/20'
            : 'hover:border-muted-foreground/40',
      )}
    >
      <NodeIcon>{icon}</NodeIcon>
      {label}
    </button>
  );
}

/** The trailing "+" affordance at the end of an action branch. */
export function AddNodeButton({
  'aria-label': ariaLabel,
  onClick,
}: {
  'aria-label': string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-full border bg-card text-muted-foreground shadow-xs transition-colors outline-none hover:text-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <PlusIcon className="size-4" />
    </button>
  );
}

// Connector geometry: a forked split below a branching node; single SVG paths
// so segments can't fall out of alignment.
const STROKE_WIDTH = 1.5;
const RADIUS = 12;
const STUB = 28; // straight run below a branching node before the fork curves
const RAIL_Y = STUB + RADIUS; // y of the horizontal rail's line center
const DESCENT = 44; // rail to child-node gap
const DROP_H = RAIL_Y + DESCENT; // full height of a branch connector
const ARROW = 5; // arrowhead half-width and depth
const ORIGIN_GAP = 8; // gap between a node and the rounded start of its line
const ARROW_GAP = 10; // gap between the arrow tip and the node below
const ELBOW_W = RADIUS * 2 + 2; // width of the per-column connector svg

const STROKE_PROPS = {
  stroke: 'currentColor',
  strokeWidth: STROKE_WIDTH,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const arrowPath = (cx: number, tipY: number) =>
  `M${cx - ARROW} ${tipY - ARROW} L${cx} ${tipY} L${cx + ARROW} ${tipY - ARROW}`;

export function ConnectorDown({ height = 40, className }: { height?: number; className?: string }) {
  const cx = ARROW + 1;
  const tipY = height - ARROW_GAP;
  return (
    <svg
      aria-hidden
      width={cx * 2}
      height={height}
      fill="none"
      className={cn('shrink-0 text-border', className)}
    >
      <path d={`M${cx} ${ORIGIN_GAP} V${tipY} ${arrowPath(cx, tipY)}`} {...STROKE_PROPS} />
    </svg>
  );
}

export interface BranchRowItem {
  key: string;
  label?: string;
  content: ReactNode;
}

/**
 * Lays out a node's children in equal-width columns joined by connectors: one
 * item gets a straight connector, two the forked split, more a T junction.
 */
export function BranchRow({ items }: { items: BranchRowItem[] }) {
  if (items.length === 1) {
    return (
      <div className="flex flex-col items-center">
        <ConnectorDown />
        {items[0].content}
      </div>
    );
  }
  const fork = items.length === 2;
  const cx = ELBOW_W / 2;
  return (
    <div className="relative grid auto-cols-fr grid-flow-col">
      {fork ? (
        <svg
          aria-hidden
          width={ELBOW_W}
          height={RAIL_Y + STROKE_WIDTH}
          fill="none"
          className="absolute top-0 left-1/2 -translate-x-1/2 text-border"
        >
          <path
            d={[
              `M${cx} ${ORIGIN_GAP} V${STUB} A${RADIUS} ${RADIUS} 0 0 1 ${cx - RADIUS} ${RAIL_Y}`,
              `M${cx} ${STUB} A${RADIUS} ${RADIUS} 0 0 0 ${cx + RADIUS} ${RAIL_Y}`,
            ].join(' ')}
            {...STROKE_PROPS}
          />
        </svg>
      ) : (
        <div
          aria-hidden
          className="absolute left-1/2 w-[1.5px] -translate-x-1/2 rounded-full bg-border"
          style={{ top: ORIGIN_GAP, height: RAIL_Y + STROKE_WIDTH / 2 - ORIGIN_GAP }}
        />
      )}
      {items.map((item, index) => (
        <BranchColumn
          key={item.key}
          label={item.label}
          fork={fork}
          position={index === 0 ? 'first' : index === items.length - 1 ? 'last' : 'middle'}
        >
          {item.content}
        </BranchColumn>
      ))}
    </div>
  );
}

interface BranchColumnProps {
  position: 'first' | 'middle' | 'last';
  label?: string;
  fork: boolean;
  children: ReactNode;
}

function BranchColumn({ position, label, fork, children }: BranchColumnProps) {
  const cx = ELBOW_W / 2;
  const tipY = DROP_H - ARROW_GAP;
  const drop = `V${tipY} ${arrowPath(cx, tipY)}`;
  const path =
    position === 'first'
      ? `M${cx + RADIUS} ${RAIL_Y} A${RADIUS} ${RADIUS} 0 0 0 ${cx} ${RAIL_Y + RADIUS} ${drop}`
      : position === 'last'
        ? `M${cx - RADIUS} ${RAIL_Y} A${RADIUS} ${RADIUS} 0 0 1 ${cx} ${RAIL_Y + RADIUS} ${drop}`
        : `M${cx} ${RAIL_Y} ${drop}`;
  return (
    // min-w-md spreads sibling branches to the design's ~448px column rhythm;
    // wider content (nested branch rows) simply overrides it
    <div
      className="relative flex min-w-md flex-col items-center px-5"
      style={{ paddingTop: DROP_H }}
    >
      {/* horizontal rail; the elbow svg below takes over at this column's
          center. A labelled rail is split into two segments with gaps around
          the label chip */}
      <span
        className={cn(
          'absolute flex -translate-y-1/2 items-center',
          position === 'first' && 'left-[calc(50%+12px)]',
          position === 'last' && 'right-[calc(50%+12px)]',
          position === 'middle' && 'right-0 left-0',
          // leave room for the fork curves around the row's center
          position === 'first' && (fork ? 'right-3' : 'right-0'),
          position === 'last' && (fork ? 'left-3' : 'left-0'),
        )}
        style={{ top: RAIL_Y }}
      >
        <span className="h-[1.5px] flex-1 rounded-full bg-border" />
        {label && (
          <>
            <span className="mx-2 rounded-md bg-muted px-2 py-0.5 text-xs font-medium whitespace-nowrap text-muted-foreground">
              {label}
            </span>
            <span className="h-[1.5px] flex-1 rounded-full bg-border" />
          </>
        )}
      </span>
      <svg
        aria-hidden
        width={ELBOW_W}
        height={DROP_H}
        fill="none"
        className="absolute top-0 left-1/2 -translate-x-1/2 text-border"
      >
        <path d={path} {...STROKE_PROPS} />
      </svg>
      {children}
    </div>
  );
}
